import { useEffect, useState, useMemo } from "react";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RSVP_LABELS, RSVP_COLORS } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Users, Plus, Search, Upload, Download, Send, MailWarning, Trash2, Crown, X, UtensilsCrossed,
} from "lucide-react";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", age_group: "adult",
  invited_by: "both", relationship: "", vip: false, dietary: "", allergies: "", city: "",
  invitation_channel: "digital", plus_one_allowed: false };

const FILTERS = [
  { k: "all", l: "Tous" }, { k: "attending", l: "Confirmés" }, { k: "awaiting", l: "En attente" },
  { k: "declined", l: "Déclinés" }, { k: "vip", l: "VIP" }, { k: "no_email", l: "Sans e-mail" },
  { k: "children", l: "Enfants" },
];

export default function Guests() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [guests, setGuests] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");

  const load = async () => { const { data } = await api.get(`/weddings/${wid}/guests`); setGuests(data); };
  const [rsvpForm, setRsvpForm] = useState(null);
  useEffect(() => { if (wid) { load(); api.get(`/weddings/${wid}/rsvp-form`).then(({ data }) => setRsvpForm(data)).catch(() => {}); } }, [wid]);

  const answerFor = (g, qu) => {
    const a = g.rsvp_answers || {};
    const v = a[qu.id] ?? a[qu.label] ?? a[qu.key];
    if (v === undefined || v === null || v === "") return null;
    return Array.isArray(v) ? v.join(", ") : String(v);
  };

  const filtered = useMemo(() => {
    if (!guests) return [];
    return guests.filter((g) => {
      const name = `${g.first_name} ${g.last_name}`.toLowerCase();
      if (q && !name.includes(q.toLowerCase())) return false;
      if (filter === "attending") return g.rsvp_status === "attending";
      if (filter === "awaiting") return ["awaiting", "invited", "draft", "maybe"].includes(g.rsvp_status);
      if (filter === "declined") return g.rsvp_status === "declined";
      if (filter === "vip") return g.vip;
      if (filter === "no_email") return !g.email;
      if (filter === "children") return ["child", "baby"].includes(g.age_group);
      return true;
    });
  }, [guests, q, filter]);

  const stats = useMemo(() => {
    if (!guests) return {};
    return {
      total: guests.length,
      attending: guests.filter((g) => g.rsvp_status === "attending").length,
      awaiting: guests.filter((g) => ["awaiting", "invited", "draft", "maybe"].includes(g.rsvp_status)).length,
      declined: guests.filter((g) => g.rsvp_status === "declined").length,
    };
  }, [guests]);

  const save = async () => {
    try {
      if (editing) await api.patch(`/weddings/${wid}/guests/${editing}`, form);
      else await api.post(`/weddings/${wid}/guests`, form);
      toast.success("Invité enregistré");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const openEdit = (g) => { setEditing(g.id); setForm({ ...EMPTY, ...g }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const del = async (id) => { await api.delete(`/weddings/${wid}/guests/${id}`); load(); };

  const setStatus = async (g, status) => {
    await api.patch(`/weddings/${wid}/guests/${g.id}`, { rsvp_status: status });
    load();
  };

  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const bulkInvite = async () => {
    const ids = sel.length ? sel : filtered.map((g) => g.id);
    const { data } = await api.post(`/weddings/${wid}/rsvp/send`, { ids });
    toast.success(`${data.sent} invitations envoyées`);
    setSel([]); load();
  };

  const doImport = async () => {
    const lines = csvText.trim().split("\n");
    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = lines.slice(1).map((l) => {
      const vals = l.split(",");
      const o = {}; headers.forEach((h, i) => (o[h] = (vals[i] || "").trim())); return o;
    });
    const { data } = await api.post(`/weddings/${wid}/guests/import`, { rows });
    toast.success(`${data.imported} importés, ${data.skipped} ignorés`);
    setImportOpen(false); setCsvText(""); load();
  };

  const exportCsv = () => {
    window.open(`${API}/weddings/${wid}/guests/export`, "_blank");
  };
  const exportCaterer = () => {
    window.open(`${API}/weddings/${wid}/guests/export-caterer`, "_blank");
  };

  if (!guests) return <Loader />;

  return (
    <div>
      <PageHeader eyebrow="Gestion des invités" title="Vos invités"
        description={`${stats.attending} confirmés · ${stats.awaiting} en attente · ${stats.declined} déclinés · ${stats.total} au total`}
        actions={<>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-btn"><Upload className="w-4 h-4 mr-1" /> Importer</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-btn"><Download className="w-4 h-4 mr-1" /> Export complet</Button>
          <Button variant="outline" onClick={exportCaterer} data-testid="export-caterer-btn"><UtensilsCrossed className="w-4 h-4 mr-1" /> Export traiteur</Button>
          <Button onClick={openNew} data-testid="add-guest-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
        </>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="guest-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 h-10" /></div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`filter-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-sm border ${filter === f.k ? "bg-stone-900 text-white" : "bg-white text-stone-600"}`}>{f.l}</button>
          ))}
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-stone-900 text-white text-sm">
          <span>{sel.length} sélectionné(s)</span>
          <Button size="sm" variant="secondary" onClick={bulkInvite} data-testid="bulk-invite"><Send className="w-4 h-4 mr-1" /> Envoyer invitations</Button>
          <button onClick={() => setSel([])} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="Aucun invité pour l'instant"
          description="Ajoutez vos premiers invités manuellement ou importez votre liste."
          action={<Button onClick={openNew} className="rounded-full"><Plus className="w-4 h-4 mr-1" /> Ajouter un invité</Button>} />
      ) : (
        <div className="card-lux overflow-hidden">
          <div className="hidden lg:grid grid-cols-[40px_1fr_140px_120px_160px_100px] gap-3 px-5 py-3 border-b text-xs uppercase tracking-wide text-stone-400">
            <div></div><div>Invité</div><div>RSVP</div><div>Menu</div><div>Contact</div><div></div>
          </div>
          {filtered.map((g) => (
            <div key={g.id} data-testid={`guest-row-${g.id}`}
              className="grid lg:grid-cols-[40px_1fr_140px_120px_160px_100px] gap-3 px-5 py-3 border-b last:border-0 items-center hover:bg-accent/20">
              <Checkbox checked={sel.includes(g.id)} onCheckedChange={() => toggleSel(g.id)} />
              <div className="cursor-pointer" onClick={() => openEdit(g)}>
                <div className="font-medium flex items-center gap-1.5">{g.first_name} {g.last_name}
                  {g.vip && <Crown className="w-3.5 h-3.5 text-champagne" />}</div>
                <div className="text-xs text-stone-400">{g.relationship || g.age_group} {g.city && `· ${g.city}`}</div>
              </div>
              <select value={g.rsvp_status || "draft"} onChange={(e) => setStatus(g, e.target.value)}
                data-testid={`guest-status-${g.id}`}
                className={`text-xs rounded-full px-2.5 py-1 border ${RSVP_COLORS[g.rsvp_status] || RSVP_COLORS.draft}`}>
                {Object.entries(RSVP_LABELS).slice(0, 5).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div className="text-sm text-stone-500">{g.meal_choice || "—"}</div>
              <div className="text-xs text-stone-400 truncate">{g.email || "Pas d'e-mail"}</div>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => del(g.id)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier l'invité" : "Nouvel invité"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input data-testid="guest-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-mail</Label><Input data-testid="guest-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-10" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <select value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-2">
                  <option value="adult">Adulte</option><option value="child">Enfant</option><option value="baby">Bébé</option></select></div>
              <div><Label>Invité par</Label>
                <select value={form.invited_by} onChange={(e) => setForm({ ...form, invited_by: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-2">
                  <option value="both">Les deux</option><option value="partner1">Partenaire 1</option><option value="partner2">Partenaire 2</option></select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Canal d'invitation</Label>
                <select data-testid="guest-channel" value={form.invitation_channel} onChange={(e) => setForm({ ...form, invitation_channel: e.target.value })} className="mt-1 h-10 w-full rounded-md border px-2">
                  <option value="digital">Numérique (e-mail)</option><option value="postal">Postal / imprimé</option><option value="both">Les deux</option></select></div>
              <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-10" /></div>
            </div>
            <div><Label>Régime / allergies</Label><Input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} className="mt-1 h-10" placeholder="Végétarien, sans gluten…" /></div>
            <label className="flex items-center gap-2"><Checkbox checked={form.vip} onCheckedChange={(v) => setForm({ ...form, vip: !!v })} /> <span className="text-sm">Invité VIP</span></label>

            {editing && (
              <div className="rounded-xl border bg-accent/10 p-3" data-testid="guest-rsvp-answers">
                <div className="text-xs uppercase tracking-wide text-stone-400 mb-2">Réponses RSVP</div>
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-stone-500">Participation</dt>
                    <dd className="font-medium text-right">{RSVP_LABELS[form.rsvp_status] || "En attente"}</dd></div>
                  {form.meal_choice && <div className="flex justify-between gap-3"><dt className="text-stone-500">Menu</dt><dd className="font-medium text-right">{form.meal_choice}</dd></div>}
                  {form.allergies && <div className="flex justify-between gap-3"><dt className="text-stone-500">Allergies</dt><dd className="font-medium text-right">{form.allergies}</dd></div>}
                  {form.dietary && <div className="flex justify-between gap-3"><dt className="text-stone-500">Restrictions</dt><dd className="font-medium text-right">{form.dietary}</dd></div>}
                  {(rsvpForm?.questions || []).map((qu) => {
                    const ans = answerFor(form, qu);
                    if (ans === null) return null;
                    return <div key={qu.id || qu.label} className="flex justify-between gap-3"><dt className="text-stone-500 break-words">{qu.label}</dt><dd className="font-medium text-right break-words">{ans}</dd></div>;
                  })}
                  {!form.meal_choice && !form.allergies && !form.dietary && !(rsvpForm?.questions || []).some((qu) => answerFor(form, qu) !== null) && form.rsvp_status !== "attending" && form.rsvp_status !== "declined" && (
                    <p className="text-xs text-stone-400">Cet invité n'a pas encore répondu.</p>
                  )}
                </dl>
              </div>
            )}
          </div>
          <DialogFooter><Button data-testid="save-guest" onClick={save} className="rounded-full">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent><DialogHeader><DialogTitle>Importer des invités (CSV)</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">Collez vos données CSV. Première ligne = en-têtes
            (first_name, last_name, email, age_group, dietary…).</p>
          <Button variant="link" className="justify-start px-0" onClick={() => window.open(`${API}/weddings/${wid}/guests/template`, "_blank")}>
            Télécharger le modèle</Button>
          <Textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={8}
            placeholder="first_name,last_name,email&#10;Jean,Dupont,jean@example.ch" className="font-mono text-xs" />
          <DialogFooter><Button onClick={doImport} className="rounded-full">Importer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
